/**
 * Environment-based configuration
 */
import { LoggingConfig } from '@logging/logging-config';
import { ApiPaths } from './api-paths';

export interface EnvironmentSettings {
  /**
   * The name of this environment
   */
  environment: EnvironmentNames ;
  /**
   * The app name taken from package.json
   */
  appName: string;
  /**
   * The app version taken from package.json
   */
  appVersion: string;
  /**
   * Production flag for build
   */
  production: boolean;
  /**
   * Logging config containing log thresholds and remote log api path
   */
  loggingConfig: LoggingConfig;
  /**
   * Toggles angular-material animations which can be problematic during Playwright E2E tests
   */
  noAnimations?: boolean;
  /**
   * Application ID
   */
  appId: string;
  /**
   * Controls whether the user sees a confirmation when they unload the app in the browser if they have a dirty edit form.
   * It is required to be disabled in Playwright E2E tests (see environment.e2e.ts)
   */
  suppressBeforeUnloadEvent: boolean;
  /**
   * root url to the CandC api
   */
  apiRoot: string;
  /**
   * API endpoint paths
   */
  apiPaths: ApiPaths;
  /**
   * service worker / progressive web app disabled.
   */
  pwaDisabled: boolean;
  /**
   * Crypto salt for encryption
   */
  cryptoSalt: string;
  /**
   * Graph URI for Microsoft Graph API
   */
  graphUri: string;
  /**
   * Toggles whether to render the Microsoft login page
   */
  renderMsLogin: boolean;
  /**
   * MSAL settings for authentication
   */
  msalSettings: MsalSettings;
  /**
   * Active areas for the application
   */
  activeAreas: string[];
  /**
   * Cache duration in minutes
   */
  cacheDurationInMinutes?: number;

  /**
   * Google Maps JavaScript API key used by map components.
   */
  googleMapsApiKey?: string;
}

export const activeAreas = {
  MarketMessenger: 'Messenger',
  MessageVault: 'Message Vault',
  Reports: 'Reports',
  Settings: 'Settings',
}

export interface MsalSettings {
  clientId: string;
  redirect: string;
  authority: string;
  verboseLogging: boolean;
}

export type EnvironmentNames =
  | 'local'
  | 'production'
  | 'sit'
  | 'uat'
  | 'development'
  | 'e2e';
